import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Users, Lock, User as UserIcon, Globe, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import GlowBackground from '../components/GlowBackground';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialsFor = (m) => {
  const src = m.first_name || m.display_name || m.email || '';
  return src.trim().charAt(0).toUpperCase() || '?';
};

const MemberCard = ({ m }) => {
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ')
    || m.display_name || m.email.split('@')[0];
  return (
    <Link
      to={`/profile/${m.user_id}`}
      data-testid={`directory-card-${m.user_id}`}
      className="group rounded-2xl bg-white/50 border border-charcoal/5 p-4 flex items-start gap-3 hover:bg-orange-primary/5 hover:border-orange-primary/20 transition-colors"
    >
      <div className="w-12 h-12 rounded-full bg-orange-primary/10 flex items-center justify-center text-orange-primary font-serif text-lg flex-shrink-0 overflow-hidden">
        {m.avatar_url
          ? <img src={m.avatar_url} alt={name} className="w-full h-full object-cover" />
          : initialsFor(m)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-medium text-charcoal truncate">{name}</div>
          {m.is_self && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-orange-primary/10 text-orange-primary">
              You
            </span>
          )}
        </div>
        {m.position && (
          <div className="text-sm text-charcoal/60 truncate flex items-center gap-1 mt-0.5">
            <UserIcon size={12} className="text-charcoal/30" />{m.position}
          </div>
        )}
        {m.timezone && (
          <div className="text-xs text-charcoal/40 truncate flex items-center gap-1 mt-0.5">
            <Globe size={11} />{m.timezone}
          </div>
        )}
      </div>
    </Link>
  );
};

const Directory = () => {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { setLoading(false); return; }
    setLoading(true); setError(null);
    axios.get(`${API}/directory`)
      .then(r => setData(r.data))
      .catch(err => {
        setError(err?.response?.data?.detail || 'Could not load directory.');
      })
      .finally(() => setLoading(false));
  }, [authLoading, isAuthenticated]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFF8F0' }}>
        <div className="w-10 h-10 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FFF8F0' }}>
        <Header />
        <div className="flex-1 flex items-center justify-center px-6 text-center">
          <div>
            <Lock size={32} className="mx-auto mb-3 text-charcoal/30" />
            <p className="text-charcoal/60 mb-4">Sign in to see your team directory.</p>
            <Link to="/" className="text-orange-primary hover:underline text-sm">Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FFF8F0' }}>
        <Header />
        <div className="flex-1 flex items-center justify-center px-6 text-center">
          <div>
            <Users size={32} className="mx-auto mb-3 text-charcoal/30" />
            <p className="text-charcoal/60 mb-1">{error}</p>
            {!user?.is_org_member && (
              <p className="text-charcoal/40 text-sm mb-4 max-w-sm">
                Ask your workspace admin to add you to your organisation to unlock this view.
              </p>
            )}
            <Link to="/" className="text-orange-primary hover:underline text-sm">Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  const members = (data?.members || []).filter(m => {
    if (!q) return true;
    return [m.first_name, m.last_name, m.display_name, m.position, m.email]
      .filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: '#FFF8F0' }}>
      <GlowBackground />
      <Header />

      <main className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center justify-between mb-6">
          <Link
            to="/"
            data-testid="directory-back"
            className="inline-flex items-center gap-1.5 text-sm text-charcoal/60 hover:text-charcoal transition-colors"
          >
            <ArrowLeft size={14} /> Back to home
          </Link>
          <Link
            to="/profile"
            data-testid="go-to-my-profile"
            className="inline-flex items-center gap-1.5 text-sm text-orange-primary hover:underline"
          >
            <UserIcon size={14} /> My profile
          </Link>
        </div>

        <motion.header
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="font-serif text-4xl text-charcoal">Team Directory</h1>
          <p className="text-charcoal/60 mt-1">
            Your teammates at <span className="text-orange-primary">{data?.organisation}</span>.
            Click anyone to see their Working With Me profile.
          </p>
        </motion.header>

        <div className="mb-5">
          <input
            type="text"
            placeholder="Search by name or role…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            data-testid="directory-search"
            className="w-full sm:max-w-sm px-3 py-2 rounded-lg border border-charcoal/10 bg-white/70 text-sm text-charcoal focus:outline-none focus:border-orange-primary/50 transition-colors"
          />
        </div>

        {members.length === 0 ? (
          <div className="text-center py-12 text-charcoal/40 text-sm">
            {query ? 'No teammates match that search.' : 'No teammates yet.'}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
          >
            {members.map(m => <MemberCard key={m.user_id} m={m} />)}
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default Directory;
